import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Languages, Plus, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type { NotificationCopy, Template } from '../../lib/types';

/**
 * Reusable push copy, in as many languages as have been written.
 *
 * Every device receives the copy in its own language where a translation
 * exists and English otherwise, so English is always required — it is the
 * fallback the whole scheme rests on.
 */
export function TemplatesTab() {
  const t = useT();
  const [editing, setEditing] = useState<Template | 'new' | null>(null);

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Template[]>('/api/notifications/templates'),
  });

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardHeader
            title={t('notif.templates.title')}
            description={t('notif.templates.description')}
            action={
              <Button
                variant="primary"
                icon={<Plus size={15} />}
                onClick={() => setEditing('new')}
              >
                {t('notif.templates.new')}
              </Button>
            }
          />
        </Card>

        {templates.isError && <ErrorNote error={templates.error} />}

        {templates.isPending ? (
          <Skeleton className="h-64" />
        ) : (templates.data?.length ?? 0) === 0 ? (
          <Card>
            <EmptyState
              icon={<Languages size={28} />}
              title={t('notif.templates.emptyTitle')}
              description={t('notif.templates.emptyDescription')}
              action={
                <Button variant="primary" onClick={() => setEditing('new')}>
                  {t('notif.templates.emptyAction')}
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="grid gap-3 xl:grid-cols-2">
            {templates.data?.map((template) => (
              <Card key={template.id}>
                <button
                  type="button"
                  onClick={() => setEditing(template)}
                  className="w-full px-4 py-3.5 text-left transition-colors hover:bg-raised sm:px-5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-ink">
                      {template.name}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge tone="brand">
                        {t('notif.compose.languages', {
                          count: template.locales.length,
                        })}
                      </Badge>
                      {template.usedByRules > 0 && (
                        <Badge>
                          {t('notif.templates.usedBy', {
                            count: template.usedByRules,
                          })}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <p className="mt-0.5 font-mono text-xs text-ink-3">
                    {template.key}
                  </p>

                  <p className="mt-2 text-sm text-ink">
                    {englishOf(template)?.title}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-2">
                    {englishOf(template)?.body}
                  </p>

                  {template.placeholders.length > 0 && (
                    <p className="mt-2 font-mono text-[11px] text-ink-3">
                      {template.placeholders.map((p) => `{{${p}}}`).join(' ')}
                    </p>
                  )}
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <TemplateEditor
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function englishOf(template: Template): NotificationCopy | undefined {
  return (
    template.locales.find((locale) => locale.language === 'en') ??
    template.locales[0]
  );
}

function TemplateEditor({
  template,
  onClose,
}: {
  template: Template | null;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'save' | 'delete' | null>(null);

  const [key, setKey] = useState(template?.key ?? '');
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [locales, setLocales] = useState<NotificationCopy[]>(
    template?.locales ?? [{ language: 'en', title: '', body: '' }],
  );

  const save = useMutation({
    mutationFn: (reason: string) => {
      const body = {
        key,
        name,
        description: description || undefined,
        locales: locales.filter(
          (locale) => locale.language && locale.title && locale.body,
        ),
        reason,
      };

      return template
        ? api.patch(`/api/notifications/templates/${template.id}`, body)
        : api.post('/api/notifications/templates', body);
    },
    onSuccess: () => {
      toast.success(
        t(template ? 'notif.templates.updated' : 'notif.templates.created'),
      );
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (reason: string) =>
      api.delete(`/api/notifications/templates/${template!.id}`, { reason }),
    onSuccess: () => {
      toast.success(t('notif.templates.deleted'));
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const update = (index: number, patch: Partial<NotificationCopy>) =>
    setLocales((current) =>
      current.map((locale, i) => (i === index ? { ...locale, ...patch } : locale)),
    );

  const hasEnglish = locales.some(
    (locale) => locale.language.toLowerCase() === 'en' && locale.title && locale.body,
  );

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        template
          ? t('notif.templates.editTitle', { name: template.name })
          : t('notif.templates.newTitle')
      }
      footer={
        <div className="flex gap-2">
          {template && (
            <Button
              variant="danger"
              icon={<Trash2 size={15} />}
              onClick={() => setDialog('delete')}
            >
              {t('common.delete')}
            </Button>
          )}
          <Button
            variant="primary"
            className="flex-1"
            disabled={!key.trim() || !name.trim() || !hasEnglish}
            onClick={() => setDialog('save')}
          >
            {template ? t('common.saveChanges') : t('notif.templates.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <section className="space-y-3">
          <Field
            label={t('common.name')}
            hint={t('notif.templates.nameHint')}
          >
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('notif.templates.namePlaceholder')}
            />
          </Field>
          <Field
            label={t('notif.templates.key')}
            hint={t('notif.templates.keyHint')}
          >
            <Input
              className="font-mono text-xs"
              value={key}
              onChange={(event) => setKey(event.target.value)}
              placeholder="missed_morning_nudge"
            />
          </Field>
          <Field label={t('common.description')}>
            <Textarea
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('notif.templates.section.copy')}
            </h3>
            <Button
              variant="ghost"
              className="text-xs"
              icon={<Plus size={14} />}
              onClick={() =>
                setLocales((current) => [
                  ...current,
                  { language: '', title: '', body: '' },
                ])
              }
            >
              {t('notif.templates.addLanguage')}
            </Button>
          </div>

          {!hasEnglish && (
            <p className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
              {t('notif.templates.englishRequired')}
            </p>
          )}

          <p className="text-xs text-ink-3">
            {t('notif.templates.placeholderLead')}{' '}
            <code className="font-mono">{'{{balance}}'}</code>,{' '}
            <code className="font-mono">{'{{streak}}'}</code>,{' '}
            <code className="font-mono">{'{{alarm_label}}'}</code>.{' '}
            {t('notif.templates.placeholderTail')}
          </p>

          {locales.map((locale, index) => (
            <div
              key={index}
              className="space-y-2 rounded-lg border border-line p-3"
            >
              <div className="flex items-end gap-2">
                <Field label={t('common.language')} className="w-28">
                  <Input
                    value={locale.language}
                    onChange={(event) =>
                      update(index, { language: event.target.value })
                    }
                    placeholder="en"
                  />
                </Field>
                {locales.length > 1 && (
                  <Button
                    variant="ghost"
                    className="text-xs"
                    onClick={() =>
                      setLocales((current) =>
                        current.filter((_, i) => i !== index),
                      )
                    }
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </div>
              <Field label={t('common.title')}>
                <Input
                  maxLength={120}
                  value={locale.title}
                  onChange={(event) =>
                    update(index, { title: event.target.value })
                  }
                />
              </Field>
              <Field label={t('common.body')}>
                <Textarea
                  rows={2}
                  maxLength={500}
                  value={locale.body}
                  onChange={(event) =>
                    update(index, { body: event.target.value })
                  }
                />
              </Field>
            </div>
          ))}
        </section>
      </div>

      <ConfirmDialog
        open={dialog !== null}
        title={
          dialog === 'delete'
            ? t('notif.templates.deleteTitle', { name: template?.name ?? '' })
            : template
              ? t('common.saveTheseChanges')
              : t('notif.templates.createTitle')
        }
        description={t(
          dialog === 'delete'
            ? 'notif.templates.deleteDescription'
            : 'notif.templates.saveDescription',
        )}
        destructive={dialog === 'delete'}
        loading={save.isPending || remove.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          dialog === 'delete' ? remove.mutate(reason) : save.mutate(reason)
        }
      />
    </Drawer>
  );
}
